<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click.stop>
        <div class="bg-white popup">
          <h2 class="title">{{ props.movement ? 'Editar movimiento' : 'Añadir movimiento' }}</h2>
          <img src="@/assets/images/ic_close.svg" class="close_icon" @click="closePopup()" />
          <div class="fields">
            <div class="field-group">
              <label for="username">Usuario*</label>
              <input
                id="username"
                type="text"
                placeholder="Ingrese el email del usuario"
                required
                v-model="username"
                :disabled="props.movement ? true : false" />
            </div>

            <div class="field-group">
              <label for="description">Descripción*</label>
              <input
                id="description"
                type="text"
                placeholder="Ingrese una descripción"
                v-model="description"
                required />
            </div>

            <div class="field-group">
              <label for="quantity">Cantidad*</label>
              <input
                id="quantity"
                :type="props.movement && props.movement.pending_amount != props.movement.amount ? 'text' : 'number'"
                step="0.01"
                inputmode="decimal"
                placeholder="Ingrese la cantidad total del movimiento"
                required
                v-model="quantityDisplay"
                :disabled="props.movement && props.movement.pending_amount != props.movement.amount ? true : false" />
            </div>

            <div class="field-group">
              <label for="expiration">Caducidad</label>
              <input
                id="expiration"
                type="date"
                v-model="expiration"/>
            </div>

            <div class="button-group">
              <button @click="emit('movementPopupCancelled')" class="secondary_button">Cancelar</button>
              <button @click="saveClicked()" class="main_button">Guardar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, defineEmits, defineProps } from 'vue';
  import { formatAmount } from '@/utils/helpers';
 
   const props = defineProps({
     showMovementPopup: Boolean,
     movement: Object,
   });
   
   const visible = ref(false);
   const emit = defineEmits(['movementSaved', 'movementPopupCancelled', 'errorHappened']);
 
   // Form fields
   const username = ref('');
   const description = ref('');
   const quantity = ref('');
   const expiration = ref('');
   const quantityDisplay = ref('');
 
   watch(() => props.showMovementPopup, (newVal) => {
     visible.value = newVal;
   });
 
   watch(() => props.movement, (newVal) => {
     if (newVal) {
       username.value = newVal.user_name || '';
       description.value = newVal.title || '';
       
       // Handle quantity display logic
       if (newVal.pending_amount !== newVal.amount) {
         quantityDisplay.value = `Disponibles ${formatAmount(newVal.pending_amount)}€ de ${formatAmount(newVal.amount)}€`;
       } else {
         quantityDisplay.value = newVal.amount;
         quantity.value = newVal.amount;
       }
 
       // Handle expiration date format
       if (newVal.expiration_date) {
         const [datePart] = newVal.expiration_date.split(' ');
         const [day, month, year] = datePart.split('/');
         const pad = (v) => v.toString().padStart(2, '0');
         expiration.value = `${year}-${pad(month)}-${pad(day)}`;
       } else {
         expiration.value = '';
       }
     } else {
       // Reset fields for new movement
       username.value = '';
       description.value = '';
       quantity.value = '';
       quantityDisplay.value = '';
       expiration.value = '';
     }
   });
 
   // Watch quantityDisplay for manual input when not disabled
   watch(quantityDisplay, (newVal) => {
     if (!props.movement || props.movement.pending_amount === props.movement.amount) {
       quantity.value = newVal;
     }
   });
 
   const closePopup = () => {
     visible.value = false;
     emit('movementPopupCancelled');
   };
 
   const is_empty = (value) => {
     return value === null || value === undefined || String(value).trim() === '';
   };
 
   const saveClicked = async () => {
     // Use reactive variables instead of DOM access
     const wallet_user_email = username.value;
     const desc = description.value;
     const qty = quantity.value;
     const exp = expiration.value;
       
     if (is_empty(desc) || (!props.movement && is_empty(qty)) || (props.movement && props.movement.pending_amount === props.movement.amount && is_empty(qty))) {
       showError('Por favor, complete todos los campos obligatorios.');
       return;
     }
 
     let response = null;
     if (props.movement != null) {    
       const editMovementData = {
         movement_id: props.movement.id,
         title: desc,
         amount: parseFloat(qty),
         expiration_date: exp,
       };
       response = await editMovementAPICall(editMovementData);
     } else {
       const addMovementData = {
         wallet_user_email: wallet_user_email,
         title: desc,
         amount: parseFloat(qty),
         expiration_date: exp,
       };
       response = await addMovementAPICall(addMovementData);
     }
 
     if (!response.ok) {
       showError("Se ha producido un error guardando el movimiento.");
       return;
     }
 
     const data = await response.json();
 
     if (data.errors == true) {
       showError(data.message);
     } else {
       emit('movementSaved');
     }
   };
 
 async function addMovementAPICall(movementData) {
   return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/add", {
       method: "POST",
       headers: {
         "Content-Type": "application/json",
         "vgod": process.env.VUE_APP_API_VGOD,
         "cp-token": process.env.VUE_APP_API_CP_TOKEN,
         "Authorization": "Bearer " + localStorage.getItem('token')
       },
       body: JSON.stringify({
         "user_id": localStorage.getItem('user_id'),
         "wallet_user_email": movementData.wallet_user_email,
         "title": movementData.title,
         "amount": movementData.amount,
         "expiration_date": movementData.expiration_date,
       }),
 
     });
 }
 
 async function editMovementAPICall(movementData) {
   return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/edit", {
       method: "POST",
       headers: {
         "Content-Type": "application/json",
         "vgod": process.env.VUE_APP_API_VGOD,
         "cp-token": process.env.VUE_APP_API_CP_TOKEN,
         "Authorization": "Bearer " + localStorage.getItem('token')
       },
       body: JSON.stringify({
         "user_id": localStorage.getItem('user_id'),
         "movement_id": movementData.movement_id,
         "title": movementData.title,
         "amount": movementData.amount,
         "expiration_date": movementData.expiration_date,
       }),
 
     });
 }
 
 const showError = async (message) => {
   emit('errorHappened', message);
 };
 
 
   </script>
   
   <style scoped>
   .fade-enter-active, .fade-leave-active {
     transition: opacity 0.3s ease;
   }
   .fade-enter, .fade-leave-to {
     opacity: 0;
   }
   
   .fixed {
     position: fixed;
     top: 0;
     left: 0;
     right: 0;
     bottom: 0;
     display: flex;
     justify-content: center;
     align-items: center;
     background-color: rgba(0, 0, 0, 0.5);
     z-index: 1000;
   }
   
   .bg-white {
     background-color: white;
     padding: 30px;
     border-radius: var(--radius-lg);
     box-shadow: var(--shadow-lg);
     width: 90%;
     max-width: 600px;
     max-height: 90vh;
     overflow-y: auto;
   }
 
   .popup {
     position: relative;
   }
 
   .close_icon {
     position: absolute;
     top: 20px;
     right: 20px;
     width: 30px;
     height: 30px;
     cursor: pointer;
     transition: transform var(--transition-fast);
   }

   .close_icon:hover {
     transform: scale(1.1);
   }
 
   .title {
     border-bottom: 1px solid var(--very-clear-gray);
     text-align: center;
     margin: 0 0 var(--spacing-lg) 0;
     padding-bottom: var(--spacing-md);
   }
 
   .fields {
     display: flex;
     flex-direction: column;
     gap: var(--spacing-md);
   }

   .field-group {
     display: flex;
     flex-direction: column;
     gap: var(--spacing-xs);
   }

   .field-group label {
     font-weight: 600;
     color: var(--text-color-dark);
     font-size: 14px;
   }
 
   .field-group input {
     width: 100%;
   }
 
   .field-group input:disabled {
     background-color: var(--almost-white) !important;
     border-color: var(--light-gray) !important;
     cursor: not-allowed;
     opacity: 0.6;
   }

   .button-group {
     display: flex;
     gap: var(--spacing-md);
     margin-top: var(--spacing-lg);
   }

   .button-group button {
     flex: 1;
   }

   @media (max-width: 768px) {
     .bg-white {
       width: 95%;
       padding: 20px;
     }

     .button-group {
       flex-direction: column;
     }
   }
   </style>
  